import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import { FORM_MODES } from 'config/formModes';
import { useFormMode, useNavigate } from 'hooks';
import { ENTITY } from 'modules/campaigns/config';
import { PHASES } from 'modules/campaigns/modules/campaigns-reports/services/campaignStatusPhaseService';
import { BID_STRATEGIES } from 'modules/campaigns/modules/common-campaign-form/config';
import { MY_AUDIENCES_TYPES } from 'modules/campaigns/modules/common-campaign-form/config/MyAudiencesTypes';
import { APPROVAL_STATES } from 'modules/campaigns/modules/common-campaign-form/config/approvalStates';
import { BID_TYPES } from 'modules/campaigns/modules/common-campaign-form/config/bidTypes';
import { TARGET_CPA_LEARNING_STATES } from 'modules/campaigns/modules/common-campaign-form/config/targetCpaLearningStates';
import { generateCampaignEditorPath } from 'modules/campaigns/services/utils';
import { setRecommendations } from 'modules/recommendations';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useModal } from 'modules/taboola-common-frontend-modules/modals';
import { CAMPAIGN_DIMENSION } from 'services/campaignDimension';
import { BYPASS_PBP_RESTRICTIONS_PERMISSION } from './consts';
import { useMyAudiences } from './useMyAudiences';
import styles from './blockPBPAudienceModal.module.scss';

const LEARNING_STATES_IN_LEARNING_PHASE = [PHASES.EXPEDITED_LEARNING, PHASES.LEARNING, PHASES.LEARNING_LIMITED];
const CVR_LEARNING_STATES_IN_LEARNING_PHASE = [PHASES.CVR_LEARNING, PHASES.CVR_RELEARNING, PHASES.CVR_LEARNING_LIMITED];
const TARGET_CPA_LEARNING_STATES_IN_LEARNING_PHASE = [
    TARGET_CPA_LEARNING_STATES.CVR_LEARNING,
    TARGET_CPA_LEARNING_STATES.CVR_LEARNING_LIMITED,
    TARGET_CPA_LEARNING_STATES.TCPA_LEARNING,
];

const getModalConfig = audienceName => ({
    title: (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.blockPBP.modal.title"
            defaultMessage="Predictive Audiences can't be added to running campaigns."
            tagName="h2"
        />
    ),
    content: (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.blockPBP.modal.content"
            defaultMessage={`To proceed, duplicate this campaign with "{audienceName}" targeted. Non-Predictive "My Audiences" will be removed, but any previously selected Predictive Audiences will remain. Do you want to continue?`}
            values={{ audienceName }}
        />
    ),
    className: styles['modal-container'],
    contentClassName: styles['content'],
    formProps: {
        submitButtonText: (
            <FormattedMessage
                id="campaign.editor.targeting.my.audiences.blockPBP.modal.submitButtonText"
                defaultMessage="Yes, Duplicate."
            />
        ),
    },
});

const calculateIsDisplayedCvrLearningStatus = ({
    cvrLearningStatus,
    targetCpaLearningStatus,
    bidType,
    bidStrategy,
    approvalState,
}) => {
    // Causes displayed CVR learning status to be null
    const isCampaignBidTypeFixed = bidType === BID_TYPES.FIXED;
    const isCampaignApproved = approvalState === APPROVAL_STATES.APPROVED;

    // Causes displayed CVR learning status wto be pending
    const isCampaignPendingApproval = approvalState === APPROVAL_STATES.PENDING;

    const isDisplayedCvrLearningStatusNotLearning =
        (!cvrLearningStatus && !isCampaignApproved) || isCampaignBidTypeFixed || isCampaignPendingApproval;

    if (isDisplayedCvrLearningStatusNotLearning) {
        return false;
    }

    const isDisplayedCvrLearningStatusLearning = !cvrLearningStatus && approvalState === APPROVAL_STATES.APPROVED;

    if (isDisplayedCvrLearningStatusLearning) {
        return true;
    }

    // Re-Learning State is not being considered in the check since it only affects labeling relearning vs learning and not whether or not the campaign is some learning state

    // DEFAULT STATE
    const isBidStratTargetCpaAndTargetCpaLearning =
        bidStrategy === BID_STRATEGIES.TARGET_CPA &&
        TARGET_CPA_LEARNING_STATES_IN_LEARNING_PHASE.includes(targetCpaLearningStatus);

    return isBidStratTargetCpaAndTargetCpaLearning || CVR_LEARNING_STATES_IN_LEARNING_PHASE.includes(cvrLearningStatus);
};

export const useBlockPBPAudience = ({ selectedAccountId }) => {
    const { open } = useModal();
    const navigate = useNavigate();
    const formMode = useFormMode();
    const dispatch = useDispatch();
    const { allSegmentsByIdMap, createNewAudience } = useMyAudiences(selectedAccountId);
    const isPbpRestrictionsBypassPermitted = usePermissions([BYPASS_PBP_RESTRICTIONS_PERMISSION]);
    const { formAccount, data: campaignData, initialData } = useFormDataContext();
    const {
        lifetimeSpent: campaignLifetimeSpent,
        startDate: campaignStartDate,
        learningState: campaignLearningState,
        cvrLearningStatus: campaignCvrLearningStatus,
        targetCpaLearningStatus: campaignTargetCpaLearningStatus,
        bidType: campaignBidType,
        bidStrategy: campaignBidStrategy,
        approvalState,
    } = initialData;

    const isConsideredNewCampaign = useMemo(() => {
        const hasSpend = campaignLifetimeSpent > 0;
        const isCampaignStartDateInFuture = moment(campaignStartDate).isAfter(moment());
        const isInLearningPhase =
            LEARNING_STATES_IN_LEARNING_PHASE.includes(campaignLearningState) ||
            calculateIsDisplayedCvrLearningStatus({
                cvrLearningStatus: campaignCvrLearningStatus,
                targetCpaLearningStatus: campaignTargetCpaLearningStatus,
                bidType: campaignBidType,
                bidStrategy: campaignBidStrategy,
                approvalState,
            });

        return !hasSpend || isCampaignStartDateInFuture || isInLearningPhase;
    }, [
        campaignBidStrategy,
        campaignBidType,
        campaignCvrLearningStatus,
        campaignLearningState,
        campaignLifetimeSpent,
        campaignStartDate,
        approvalState,
        campaignTargetCpaLearningStatus,
    ]);

    const { id: formAccountId = '' } = formAccount;
    const campaignId = get(campaignData, 'id');
    const campaignName = get(campaignData, 'name');
    const includeAudiences = get(campaignData, 'campaignTargeting.myAudienceTargeting.include', []);
    const isBlockPBPEnabled = useConfigMatch({
        [FEATURE_FLAGS.ADS_CONSOLE_BLOCK_PREDICTIVE_AUDIENCES_IN_EDIT_CAMPAIGN]: 'true',
    });

    const pbpIncludeAudiences = useMemo(
        () =>
            includeAudiences.filter(item => {
                const { id: itemId } = item;
                return allSegmentsByIdMap[itemId]?.type === MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE;
            }),
        [allSegmentsByIdMap, includeAudiences]
    );

    const isCampaignOnlyTargetingPBP = useMemo(
        () => pbpIncludeAudiences.length > 0 && pbpIncludeAudiences.length === includeAudiences.length,
        [includeAudiences, pbpIncludeAudiences]
    );

    const duplicateCampaignPath = generateCampaignEditorPath({
        mode: FORM_MODES.DUPLICATE,
        accountId: formAccountId,
        campaignId,
        dimension: CAMPAIGN_DIMENSION.SPONSORED,
        entity: ENTITY.CAMPAIGN,
    });

    const navigateToDuplicateCampaign = useCallback(
        async newAudience => {
            const updatedIncludes = [createNewAudience(newAudience.id, newAudience), ...pbpIncludeAudiences];

            dispatch(
                setRecommendations({
                    recommendationData: {
                        name: `Predictive Audience -- ${campaignName}`,
                        campaignTargeting: {
                            myAudienceTargeting: {
                                include: updatedIncludes,
                            },
                        },
                    },
                })
            );

            await navigate(duplicateCampaignPath);
        },
        [campaignName, createNewAudience, dispatch, duplicateCampaignPath, navigate, pbpIncludeAudiences]
    );

    const shouldBlockAudience = useCallback(
        audience => {
            const { audienceType } = audience;
            return (
                isBlockPBPEnabled &&
                formMode === FORM_MODES.EDIT &&
                audienceType === MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE &&
                !isPbpRestrictionsBypassPermitted &&
                !isConsideredNewCampaign &&
                !isCampaignOnlyTargetingPBP
            );
        },
        [
            formMode,
            isBlockPBPEnabled,
            isCampaignOnlyTargetingPBP,
            isConsideredNewCampaign,
            isPbpRestrictionsBypassPermitted,
        ]
    );

    const openBlockPBPAudienceModal = useCallback(
        async audience => {
            const { name: audienceName } = audience;
            const result = await open(getModalConfig(audienceName));
            if (result) {
                navigateToDuplicateCampaign(audience);
            }
        },
        [navigateToDuplicateCampaign, open]
    );

    return { openBlockPBPAudienceModal, shouldBlockAudience, navigateToDuplicateCampaign };
};
